import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { fmtIST } from "@/lib/datetime";
import { downloadPassJpeg, downloadPassPdf } from "@/lib/passExport";
import { CheckCircle, Clock, Warning, Prohibit, Printer, IdentificationCard, WhatsappLogo, FilePdf, Image } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Anonymous axios — no cookies, no auth interceptor. Visitors open this page
// on their own phone from a WhatsApp / email link.
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const STATUS = {
  active:    { title: "Active",    tone: "emerald", Icon: CheckCircle, msg: "Show this pass at the gate." },
  scheduled: { title: "Scheduled", tone: "sky",     Icon: Clock,       msg: "This pass is scheduled for a future date." },
  expired:   { title: "Expired",   tone: "slate",   Icon: Warning,     msg: "This pass is no longer valid." },
  exhausted: { title: "Used",      tone: "slate",   Icon: CheckCircle, msg: "This pass has been fully used." },
  revoked:   { title: "Revoked",   tone: "rose",    Icon: Prohibit,    msg: "This pass has been revoked." },
};

const toneStyles = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  sky:     { bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-700" },
  slate:   { bg: "bg-slate-100",  border: "border-slate-200",   text: "text-slate-600" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-700" },
};

function formatDT(iso) {
  return fmtIST(iso, { date: true, time: true });
}

export default function PublicVisitorPass() {
  const { passId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pass, setPass] = useState(null);
  const [error, setError] = useState(null);
  const [busyDl, setBusyDl] = useState(null); // 'pdf' | 'jpg' | null
  const cardRef = useRef(null);

  useEffect(() => {
    publicApi.get(`/gate-check/passes/${passId}`)
      .then((r) => setPass(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Pass not found"));
  }, [passId]);

  const doDownload = async (kind) => {
    if (!cardRef.current) return;
    setBusyDl(kind);
    try {
      const fname = `visitor-pass-${passId}.${kind}`;
      if (kind === "pdf") await downloadPassPdf(cardRef.current, fname);
      else await downloadPassJpeg(cardRef.current, fname);
    } finally { setBusyDl(null); }
  };

  // /pass/{id}?dl=pdf|jpg → auto-download once loaded (used by the
  // Pre-Register success dialog buttons so every output shares this design).
  const dl = searchParams.get("dl");
  useEffect(() => {
    if (!pass || !dl) return;
    const t = setTimeout(async () => {
      await doDownload(dl === "pdf" ? "pdf" : "jpg");
      setSearchParams({}, { replace: true });
    }, 500); // let fonts/QR paint first
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass, dl]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-md p-6 text-center">
          <Warning size={48} className="text-rose-500 mx-auto mb-3" weight="fill" />
          <div className="text-lg font-semibold text-slate-800">Pass unavailable</div>
          <div className="text-sm text-slate-500 mt-1">{error}</div>
        </div>
      </div>
    );
  }
  if (!pass) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading pass…</div>;
  }

  const status = STATUS[pass.status] || STATUS.active;
  const tone = toneStyles[status.tone];
  const shareUrl = `${window.location.origin}/pass/${pass.pass_id}`;

  // WhatsApp share — pre-fills a friendly message with the pass URL. Falls back
  // to the generic share screen when no phone is on file.
  const openWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hi ${pass.visitor_name}, here is your Visitor Pass for BBS Global.\nOpen this link on your phone at the gate:\n${shareUrl}`,
    );
    const digits = (pass.visitor_phone || "").replace(/\D/g, "");
    const target = digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-10 print:bg-white print:p-0">
      <div className="max-w-md mx-auto">
        {/* Header — visible only on screen */}
        <div className="print:hidden flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 text-[13px] text-slate-500">
            <IdentificationCard size={18} weight="bold" className="text-brand" />
            <span className="font-semibold text-slate-700">BBS Global · CheckPoint</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={openWhatsApp}
              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
              data-testid="public-whatsapp-btn"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </button>
            <button
              onClick={() => doDownload("pdf")}
              disabled={busyDl === "pdf"}
              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
              data-testid="public-pdf-btn"
            >
              <FilePdf size={14} /> {busyDl === "pdf" ? "…" : "PDF"}
            </button>
            <button
              onClick={() => doDownload("jpg")}
              disabled={busyDl === "jpg"}
              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50"
              data-testid="public-jpg-btn"
            >
              <Image size={14} /> {busyDl === "jpg" ? "…" : "JPG"}
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
              data-testid="public-print-btn"
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        {/* The pass */}
        <div ref={cardRef} className="pass-card bg-white border-2 border-brand rounded-lg overflow-hidden shadow-sm print:shadow-none">
          {/* Burgundy header strip */}
          <div className="bg-brand text-white p-4 flex items-center justify-between">
            <div>
              <div className="text-[9px] tracking-widest uppercase text-white/80 font-semibold">Visitor Pass</div>
              <div className="font-black text-2xl leading-none mt-1 tracking-tight">CheckPoint</div>
            </div>
            <div className="text-right">
              <div className="text-[9px] tracking-widest uppercase text-white/80 font-semibold">Pass ID</div>
              <div className="font-mono text-xs mt-1">{pass.pass_id}</div>
            </div>
          </div>

          {/* Status pill */}
          <div className={`px-5 pt-4 pb-2 border-b ${tone.border}`}>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${tone.bg} ${tone.border} ${tone.text} text-[12px] font-semibold`} data-testid="public-status-pill">
              <status.Icon size={14} weight="fill" />
              {status.title}
            </div>
            <div className={`text-[11.5px] mt-1.5 ${tone.text}`}>{status.msg}</div>
          </div>

          {/* Visitor block */}
          <div className="p-5 space-y-4">
            <div>
              <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Visitor</div>
              <div className="text-lg font-bold text-slate-800 mt-0.5" data-testid="public-visitor-name">{pass.visitor_name}</div>
              <div className="text-[12px] text-slate-500">{pass.company || "Individual"} · {pass.visitor_phone}</div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div>
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Host</div>
                <div className="text-slate-700">{pass.host_name || "—"}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Gate</div>
                <div className="text-slate-700">{pass.gate_name || "Any gate"}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Valid from</div>
                <div className="text-slate-700">{formatDT(pass.valid_from)}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Valid until</div>
                <div className="text-slate-700">{formatDT(pass.valid_until)}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Purpose</div>
                <div className="text-slate-700">{pass.purpose}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[9px] tracking-widest uppercase text-slate-500 font-semibold">Usage</div>
                <div className="text-slate-700">
                  {pass.usage_mode === "single" ? "Single-use" : `Multi-use · ${pass.use_count}/${pass.max_uses} used`}
                </div>
              </div>
            </div>

            {/* QR */}
            <div className="flex flex-col items-center pt-3 border-t border-slate-100">
              <div className="bg-white p-2 rounded-md border border-slate-200">
                <QRCodeCanvas value={shareUrl} size={172} level="M" includeMargin={false} />
              </div>
              <div className="text-[10px] text-slate-400 mt-2 text-center">
                Guard scans this at the gate to check you in instantly.
              </div>
            </div>
          </div>

          {/* Footer strip */}
          <div className="bg-slate-50 border-t border-slate-200 px-5 py-2 text-[9px] text-slate-400 tracking-widest uppercase font-semibold text-center print:bg-white">
            BBS Global Pvt. Ltd. · Internal use only
          </div>
        </div>
      </div>

      <style>{`
        @page { size: 110mm 165mm; margin: 4mm; }
        @media print {
          html, body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pass-card { max-width: 100mm !important; margin: 0 auto !important; }
          /* Shrink the QR + tighten paddings so the whole card fits one page */
          .pass-card canvas { width: 120px !important; height: 120px !important; }
          .pass-card .p-5 { padding: 10px !important; }
          .pass-card .p-4 { padding: 8px !important; }
          .pass-card .space-y-4 > * + * { margin-top: 6px !important; }
          .pass-card .pt-3 { padding-top: 6px !important; }
        }
      `}</style>
    </div>
  );
}
